/** Iter 639 — vendor panel split · Step 3: Stripe Connect payouts
 * (moved verbatim from pages/PartnerProgramPage.jsx). */
import React, { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { ENGAGEMENT_HEADERS, ENGAGEMENT_PARAM, errMsg } from "@/features/vendorPanel/helpers";

export default function StripeConnectCard({ v }) {
  const { setStep } = v;
  const nextStep = () => setStep(4);
  const onBack = () => setStep(2);
  const connectMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(
        "/classifieds/engagement/vendors/me/connect-link",
        {},
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        // Full-page redirect — Stripe hosts the onboarding UI
        window.location.href = data.url;
      } else {
        toast.success("Stripe account already connected.");
        nextStep();
      }
    },
    onError: (err) => toast.error(errMsg(err, "Couldn't start Stripe connect")),
  });

  // If Stripe redirected us back with ?connected=1, jump to done.
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    if (search.get("connected") === "1") setStep(4);
  }, [setStep]);

  return (
    <div className="rounded-2xl bg-white shadow-card p-6 md:p-8 space-y-4" data-testid="partner-stripe-card">
      <h2 className="font-display text-2xl font-black text-slate-900">Connect your payouts</h2>
      <p className="text-sm text-slate-500">
        We use Stripe to send payouts straight to your NZ bank. It takes 2–3 minutes.
      </p>

      <ul className="grid gap-2 text-sm text-slate-600">
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <span>Direct bank payouts — no waiting on manual bank transfers.</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <span>Stripe handles the compliance; Allsale never sees your bank details.</span>
        </li>
        <li className="flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <span>You can pause or disconnect anytime from Account → Payouts.</span>
        </li>
      </ul>

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-semibold text-slate-500 hover:text-slate-900"
          data-testid="partner-stripe-back"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={() => connectMut.mutate()}
          disabled={connectMut.isPending}
          data-testid="partner-stripe-connect"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-6 py-3 disabled:opacity-70 transition-colors"
        >
          {connectMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
          Connect with Stripe
        </button>
      </div>

      <button
        type="button"
        onClick={nextStep}
        className="text-xs text-slate-400 hover:text-slate-600 underline"
        data-testid="partner-stripe-skip"
      >
        I&apos;ll set this up later
      </button>
    </div>
  );
}
